import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { approvalTheme } from '../theme';
import { ApprovalButton } from '../actions/ApprovalButton';

interface ApprovalEmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };
  className?: string;
}

export const ApprovalEmptyState: React.FC<ApprovalEmptyStateProps> = ({
  icon: Icon,
  title,
  description,
  action,
  className,
}) => {
  return (
    <div className={cn('flex flex-col items-center justify-center py-16 px-6 text-center', className)}>
      {/* Icon */}
      <div
        className="w-20 h-20 rounded-full flex items-center justify-center mb-6 accent-info bg-[var(--accent-bg-outline)] border-2"
        style={{
          borderColor: 'hsl(var(--accent-info-500) / 0.3)',
        }}
      >
        <Icon className="w-10 h-10 accent-info text-[var(--accent-text-on-outline)]" />
      </div>

      {/* Title */}
      <h3
        className="text-foreground font-bold mb-2"
        style={{
          fontSize: approvalTheme.typography.h3.fontSize,
          fontWeight: approvalTheme.typography.h3.fontWeight,
        }}
      >
        {title}
      </h3>

      {/* Description */}
      <p
        className="text-muted-foreground max-w-md mb-6"
        style={{
          fontSize: approvalTheme.typography.body.fontSize,
          lineHeight: approvalTheme.typography.body.lineHeight,
        }}
      >
        {description}
      </p>

      {/* Action Button */}
      {action && (
        <ApprovalButton
          variant="primary"
          size="lg"
          onClick={action.onClick}
          icon={action.icon}
        >
          {action.label}
        </ApprovalButton>
      )}
    </div>
  );
};

interface ApprovalLoadingStateProps {
  message?: string;
  className?: string;
}

export const ApprovalLoadingState: React.FC<ApprovalLoadingStateProps> = ({
  message = 'Loading...',
  className,
}) => {
  return (
    <div className={cn('flex flex-col items-center justify-center py-16 px-6', className)}>
      {/* Spinner */}
      <div className="relative w-16 h-16 mb-6">
        <div className="absolute inset-0 border-4 border-border rounded-full" />
        <div
          className="absolute inset-0 border-4 border-transparent border-t-primary rounded-full animate-spin"
          style={{
            animationDuration: '1s',
          }}
        />
      </div>

      {/* Message */}
      <p
        className="text-muted-foreground"
        style={{
          fontSize: approvalTheme.typography.body.fontSize,
        }}
      >
        {message}
      </p>
    </div>
  );
};

interface ApprovalErrorStateProps {
  title?: string;
  message: string;
  retry?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

export const ApprovalErrorState: React.FC<ApprovalErrorStateProps> = ({
  title = 'Something went wrong',
  message,
  retry,
  className,
}) => {
  return (
    <div className={cn('flex flex-col items-center justify-center py-16 px-6 text-center', className)}>
      {/* Error Icon */}
      <div
        className="w-20 h-20 rounded-full flex items-center justify-center mb-6 accent-destructive bg-[var(--accent-bg-outline)] border-2"
        style={{
          borderColor: 'hsl(var(--accent-destructive-500) / 0.3)',
        }}
      >
        <span className="text-4xl">⚠️</span>
      </div>

      {/* Title */}
      <h3
        className="text-foreground font-bold mb-2"
        style={{
          fontSize: approvalTheme.typography.h3.fontSize,
          fontWeight: approvalTheme.typography.h3.fontWeight,
        }}
      >
        {title}
      </h3>

      {/* Message */}
      <p
        className="text-muted-foreground max-w-md mb-6"
        style={{
          fontSize: approvalTheme.typography.body.fontSize,
          lineHeight: approvalTheme.typography.body.lineHeight,
        }}
      >
        {message}
      </p>

      {/* Retry Button */}
      {retry && (
        <ApprovalButton
          variant="outline"
          size="md"
          onClick={retry.onClick}
        >
          {retry.label}
        </ApprovalButton>
      )}
    </div>
  );
};
